import { useQuery } from "@tanstack/react-query";
import AppRoutes from "AppRoutes";
import { BsSwitch } from "components/Bootstrap/BsSwitch";
import { InlineSpinner } from "components/InlineSpinner";
import { getBoardRouteKey } from "helpers";
import { createLogger } from "logging";
import { getResource, isResourceKey, useApi, useBoard, useTranslations } from "providers";
import { useState } from "react";

import { AuditLogDetailRow } from "./AuditLogDetailRow";
import { AuditLogRow } from "./AuditLogRow";

const logger = createLogger("StandardMeetingsPanelPreview");

interface StandardMeetingsPanelPreviewProps {
  meetingId: number;
  templateId?: number;
}

export const StandardMeetingsPanelPreview = ({ meetingId, templateId }: StandardMeetingsPanelPreviewProps) => {
  const i18n = useTranslations();
  const { board } = useBoard();
  const { standardMeetingsApi } = useApi();
  const [showDetails, setShowDetails] = useState(false);

  // only accept to apply new standard meetings:
  // templateId === -10 || templateId === -20 ||
  // the empty/classic hard-coded id is only used for the AddMeetingForm.

  const boardKey = getBoardRouteKey(board);

  const queryResult = useQuery({
    queryKey: [`Board|${boardKey}|StandardMeetings|${meetingId}|${templateId}`],
    queryFn: async () => {
      logger.debug("useQuery(): loading", meetingId, templateId);
      if (!meetingId) return null;
      if (!templateId) return null;
      const data = await standardMeetingsApi.getPreview(meetingId, templateId);
      logger.trace("Loaded:", data);
      return data;
    },
    enabled: !!meetingId && !!templateId && !!boardKey,
  });
  const preview = queryResult.data;
  const isLoading = queryResult.isFetching || !preview;

  if (!templateId) {
    return <div>-</div>;
  }

  if (!meetingId) {
    return <div>-</div>;
  }

  if (isLoading) {
    return <InlineSpinner text={i18n.StandardMeetingsPanel_Preview_Loading} />;
  }

  const { auditLogs, template } = preview;
  if (!auditLogs) {
    return null;
  }

  if (!template) {
    return null;
  }

  if (!board) {
    return null;
  }

  const templateUrl = AppRoutes.toBoardMeeting(template.id, board);

  return (
    <>
      <div className="mt-2">
        <div className="form-group">
          <div className="sbb-label">Gewählte Vorlage:</div>
          <div>
            <a href={templateUrl} target="_blank" className="sbb-link">
              {template.title}
            </a>
          </div>
        </div>

        <div className="form-group">
          <BsSwitch
            name="ToggleDetails"
            onText={i18n.Actions_ToggleDetails}
            offText={i18n.Actions_ToggleDetails}
            checked={showDetails}
            onChange={() => {
              setShowDetails(!showDetails);
            }}
          />
        </div>
      </div>

      <table className="table sbb-table-bs table-sm">
        <thead>
          <tr>
            <th className="entityName">Entity</th>
            <th className="state">Status</th>
            {showDetails && <th className="propertyName">Property</th>}
            {showDetails && <th className="state">Property Status</th>}
            {showDetails && <th>Before</th>}
            {showDetails && <th>After</th>}
          </tr>
        </thead>

        <tbody>
          {auditLogs.map((ent, eix) => {
            const { entityName, entityId, state, items = [] } = ent;
            const key = `row:${entityId || eix}`;
            const stateLabel = isResourceKey(state) ? getResource(state) : state;

            return showDetails && items.length > 0 ? (
              <AuditLogDetailRow
                key={key}
                keyToUse={key}
                entityName={entityName}
                stateLabel={stateLabel}
                items={items}
              />
            ) : (
              <AuditLogRow key={key} keyToUse={key} entityName={entityName} stateLabel={stateLabel} />
            );
          })}
        </tbody>
      </table>
    </>
  );
};
